<template>
  <div ref="panelRef" class="PreviewPanel">
    <div class="PreviewPanel__resize-handle" @mousedown.prevent="startResize" />
    <PreviewPublishToolbar
      v-if="showHtmlPublishToolbar"
      @publish="publishModal.open()"
    />
    <div v-else class="PreviewPanel__header">
      <div class="PreviewPanel__header-start">
        <span class="PreviewPanel__title">Preview</span>
        <span v-if="previewStore.contentType" class="PreviewPanel__badge">
          {{ previewStore.contentType.toUpperCase() }}
        </span>
        <div v-if="previewTitle" class="PreviewPanel__content-title-wrap">
          <UITooltip>
            <template #trigger>
              <UITextEllipsis class="PreviewPanel__content-title">
                {{ previewTitle }}
              </UITextEllipsis>
            </template>

            <template #content>
              {{ previewTitle }}
            </template>
          </UITooltip>
        </div>
      </div>
      <div class="PreviewPanel__header-actions">
        <div id="PreviewPanel__portal" ref="previewToolbarPortalEl" />
        <button class="PreviewPanel__close" @click="previewStore.closePreview">
          ✕
        </button>
      </div>
    </div>
    <div class="PreviewPanel__body">
      <component
        :is="activeRenderer"
        v-if="activeRenderer"
        ref="rendererRef"
        :content="previewStore.rawContent"
        :isResizing="isResizing"
        v-bind="previewRendererAttrs"
      />
      <div v-else class="PreviewPanel__unsupported">
        <span class="PreviewPanel__unsupported-icon">⊘</span>
        <span class="PreviewPanel__unsupported-message">
          This content type is not supported for preview.
        </span>
        <span class="PreviewPanel__unsupported-hint">
          Supported types: HTML, Mermaid, spreadsheet (CSV / Excel), PowerPoint
          (.pptx), PDF
        </span>
      </div>
    </div>
    <HtmlPreviewViewToggle
      v-if="showHtmlViewToggle"
      class="PreviewPanel__viewToggle"
    />
    <ModalPublishToWeb
      v-if="webPublishingEnabled"
      :isOpen="publishModal.isOpen"
      @close="publishModal.close()"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, provide, ref, watch } from 'vue'

import { UITooltip } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'

import { useModal } from '@/composables/useModal'
import { useWebsitePublish } from '@/composables/useWebsitePublish'
import { isPosthogFlagOn, PosthogFeatureFlags } from '@/lib/posthog'
import {
  buildPreviewRendererAttrs,
  isHtmlGenerationComplete,
  previewPanelContentTitle,
  usePreviewStore,
} from '@/store/preview'
import ModalPublishToWeb from '@/views/components/modals/ModalPublishToWeb'
import { previewToolbarPortalKey } from '@/views/components/preview/previewToolbarPortalKey'
import { websitePublishKey } from '@/views/components/preview/websitePublishKey'

import HtmlPreviewViewToggle from './HtmlPreviewViewToggle.vue'
import PreviewPublishToolbar from './PreviewPublishToolbar.vue'
import { getRenderer } from './renderers'
import UITextEllipsis from '../ui/typography/UITextEllipsis.vue'

const previewStore = usePreviewStore()
const publishModal = useModal()
const websitePublish = useWebsitePublish()
provide(websitePublishKey, websitePublish)
const { htmlViewMode } = storeToRefs(previewStore)

const webPublishingEnabled = computed(() =>
  isPosthogFlagOn(PosthogFeatureFlags.WebPublishing),
)

const showHtmlPublishToolbar = computed(
  () => webPublishingEnabled.value && previewStore.contentType === 'html',
)

// An HTML asset card's "Publish to Web" action seeds its block via `openPreview`
// then raises `pendingPublishRequest`; open the publish modal on top and clear the
// flag. `immediate` so the request set before this panel mounts (isOpen just flipped)
// is still honored; the reactive run covers publishing while the panel is already open.
watch(
  () => previewStore.pendingPublishRequest,
  (pending) => {
    if (!pending) return
    previewStore.clearPendingPublishRequest()
    if (webPublishingEnabled.value && previewStore.contentType === 'html') {
      publishModal.open()
    }
  },
  { immediate: true },
)

const showHtmlViewToggle = computed(
  () =>
    previewStore.content?.type === 'html' &&
    (previewStore.content.isLiveDraft
      ? !!previewStore.rawContent.trim()
      : isHtmlGenerationComplete(previewStore.rawContent)),
)

const panelRef = ref<HTMLElement>()
const previewToolbarPortalEl = ref<HTMLElement | null>(null)
provide(previewToolbarPortalKey, previewToolbarPortalEl)

const rendererRef = ref<{
  notifyPreviewPanelResize?: () => void
} | null>(null)

const isResizing = ref(false)
let powerpointResizeRaf: number | null = null

/** After `flex` on the preview wrap updates, layout must flush before the PPTX viewer measures its mount box. */
function queuePowerpointRedrawAfterSplitLayout(): void {
  if (previewStore.contentType !== 'powerpoint') return
  if (powerpointResizeRaf != null) cancelAnimationFrame(powerpointResizeRaf)
  powerpointResizeRaf = requestAnimationFrame(() => {
    powerpointResizeRaf = null
    rendererRef.value?.notifyPreviewPanelResize?.()
  })
}

const activeRenderer = computed(() => getRenderer(previewStore.contentType))

const previewTitle = computed(() =>
  previewPanelContentTitle(previewStore.content),
)

const previewRendererAttrs = computed(() =>
  buildPreviewRendererAttrs(previewStore.content, {
    htmlViewMode: htmlViewMode.value,
  }),
)

const startResize = () => {
  isResizing.value = true
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}

const onResize = (e: MouseEvent) => {
  if (!isResizing.value) return
  const row =
    panelRef.value?.closest('.LayoutDashboard__workspace--split') ??
    panelRef.value?.parentElement?.parentElement
  const wrap = panelRef.value?.parentElement as HTMLElement | undefined
  if (!row || !wrap) return
  const rowRect = row.getBoundingClientRect()
  const newWidth = rowRect.right - e.clientX
  const clamped = Math.max(250, Math.min(newWidth, rowRect.width * 0.7))
  wrap.style.flex = `0 0 ${clamped}px`
  queuePowerpointRedrawAfterSplitLayout()
}

const stopResize = () => {
  isResizing.value = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  queuePowerpointRedrawAfterSplitLayout()
}

onUnmounted(() => {
  if (powerpointResizeRaf != null) cancelAnimationFrame(powerpointResizeRaf)
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<style lang="scss" scoped>
.PreviewPanel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--bg-elevated-primary, var(--bg-primary));
  border-left: 1px solid var(--border-default);
  position: relative;
  flex-shrink: 0;
  align-self: stretch;
  box-shadow: -10px 0 32px rgba(9, 9, 45, 0.05);
}

.PreviewPanel__resize-handle {
  position: absolute;
  left: -3px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;
  border-left: 1px solid var(--border-default, #e9e9ec);

  &:hover,
  &:active {
    background: var(--border-heavy);
    border-radius: 3px;
  }
}

.PreviewPanel__header,
:deep(.PreviewPublishToolbar) {
  --preview-panel-in-y: -0.375rem;

  position: relative;
  z-index: 2;
  animation: previewPanelIn 0.38s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both;
}

.PreviewPanel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-heavy);
  flex-shrink: 0;
}

.PreviewPanel__header-start {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  min-width: 0;
}

.PreviewPanel__header-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.PreviewPanel__title {
  flex-shrink: 0;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
}

.PreviewPanel__badge {
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: var(--interactive-bg-tertiary-press);
  color: var(--text-secondary);
}

.PreviewPanel__content-title-wrap {
  flex: 1;
  min-width: 0;
  overflow: hidden;

  :deep(> *) {
    display: block;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
}

.PreviewPanel__content-title {
  @include font-size-12px;
  color: var(--text-tertiary);
}

.PreviewPanel__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text-tertiary);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: var(--interactive-bg-tertiary-hover);
    color: var(--text-primary);
  }
}

.PreviewPanel__body {
  --preview-panel-in-y: 0.5rem;

  position: relative;
  z-index: 1;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  animation: previewPanelIn 0.42s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

@keyframes previewPanelIn {
  from {
    opacity: 0;
    transform: translateY(var(--preview-panel-in-y));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .PreviewPanel__header,
  :deep(.PreviewPublishToolbar),
  .PreviewPanel__body {
    animation: none;
  }
}

.PreviewPanel__unsupported {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: var(--text-secondary);
  text-align: center;
}

.PreviewPanel__unsupported-icon {
  font-size: 2rem;
  opacity: 0.5;
}

.PreviewPanel__unsupported-message {
  font-size: 0.875rem;
}

.PreviewPanel__unsupported-hint {
  font-size: 0.75rem;
  color: var(--text-tertiary);
}

.PreviewPanel__viewToggle {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 20;
}
</style>
